// ═══════════════════════════════════════════════════════════════════
// NAVBAR BEHAVIOR (static markup lives in index.html)
//
// - The <nav id="navbar"> markup is authored directly in the page so
//   crawlers see every link without JavaScript. This file only wires
//   up the interaction on top of it.
// - Desktop: one shared glassmorphism panel that slides to whichever
//   top-level item is hovered/focused, with a triangle pointer on top
//   and a hover-safe gap between the bar and the panel.
// - Mobile (<lg): hamburger + glass accordion menu (static markup in
//   the #nav-mobile block).
// - Theming: scroll state is handled by js/script.js toggling the
//   `nav-scrolled` class on #navbar; all colors live in css/style.css.
// ═══════════════════════════════════════════════════════════════════

(function () {
    const nav = document.getElementById('navbar');
    const wrap = document.getElementById('nav-menu-wrap');
    if (!nav || !wrap) return;

    const topLinks = Array.from(wrap.querySelectorAll('.nav-top-link[data-dd]'));
    const well = document.getElementById('nav-dd-well');
    const panels = Array.from(well.querySelectorAll('.nav-dd-panel'));
    let activeId = null;
    const panelHeights = {};
    const WELL_VERTICAL = 22; // 10px padding x2 + 1px border x2

    // ── Measure every panel; the shared well matches the WIDEST panel
    //    (so the slide stays stable) but its height follows the ACTIVE
    //    panel — fewer items = shorter menu, more items = longer. ──
    function measurePanels() {
        panels.forEach((p) => { p.style.display = 'flex'; p.style.width = 'max-content'; });
        panels.forEach((p) => {
            panelHeights[p.dataset.ddPanel] = p.offsetHeight;
        });
        panels.forEach((p) => { p.style.display = ''; p.style.width = ''; });
        if (activeId && panelHeights[activeId]) {
            well.style.height = panelHeights[activeId] + WELL_VERTICAL + 'px';
        }
    }

    function positionWell(itemEl) {
        const navRect = nav.getBoundingClientRect();
        const itemRect = itemEl.getBoundingClientRect();
        const itemCenter = itemRect.left - navRect.left + itemRect.width / 2;
        const w2 = well.offsetWidth / 2;
        const activePanel = well.querySelector('.nav-dd-panel.is-active');
        const linksCol = activePanel
            ? activePanel.querySelector('.nav-dd-links')
            : null;
        // Anchor the triangle to the middle of the LINKS column (not the
        // whole panel, which now also contains the image), so the nav link
        // stays centered above its links while the image extends outward.
        const anchor = linksCol
            ? linksCol.offsetLeft + linksCol.offsetWidth / 2
            : w2;
        let x = itemCenter - anchor;
        x = Math.max(4, Math.min(x, navRect.width - w2 * 2 - 4));
        well.style.setProperty('--dd-x', `${x.toFixed(1)}px`);
        well.style.setProperty('--tri-left', `${(itemCenter - x).toFixed(1)}px`);
    }

    function showPanel(id, itemEl) {
        if (activeId === id && well.classList.contains('is-open')) return;
        activeId = id;
        panels.forEach((p) => p.classList.toggle('is-active', p.dataset.ddPanel === id));
        topLinks.forEach((t) => {
            const on = t.dataset.dd === id;
            t.classList.toggle('is-open', on);
            t.setAttribute('aria-expanded', on ? 'true' : 'false');
        });
        if (panelHeights[id]) {
            well.style.height = panelHeights[id] + WELL_VERTICAL + 'px';
        }
        well.classList.add('is-open');
        positionWell(itemEl);
        panels.forEach((p) => {
            p.querySelectorAll('.nav-dd-link').forEach((a, i) => {
                a.style.transitionDelay = p.dataset.ddPanel === id ? `${Math.min(i * 0.045, 0.27)}s` : '0s';
            });
        });
    }

    function hidePanel() {
        activeId = null;
        well.classList.remove('is-open');
        topLinks.forEach((t) => {
            t.classList.remove('is-open');
            t.setAttribute('aria-expanded', 'false');
        });
    }

    topLinks.forEach((btn) => {
        btn.addEventListener('mouseenter', () => showPanel(btn.dataset.dd, btn));
        btn.addEventListener('focus', () => showPanel(btn.dataset.dd, btn));
    });

    // Stays open while the cursor crosses the gap or rests on the panel;
    // the panel is a subtree of the nav pill, so a plain mouseleave on
    // the pill (not the link row) is the safe close trigger.
    nav.addEventListener('mouseleave', hidePanel);

    nav.addEventListener('focusout', (e) => {
        if (!nav.contains(e.relatedTarget)) hidePanel();
    });

    nav.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            hidePanel();
            const open = topLinks.find((t) => t.getAttribute('aria-expanded') === 'true');
            if (open) open.focus();
        }
    });

    well.querySelectorAll('.nav-dd-link, .nav-dd-media-btn').forEach((a) => {
        a.addEventListener('click', hidePanel);
    });

    // ── Mobile menu ──
    const burger = document.getElementById('nav-hamburger');
    const mobile = document.getElementById('nav-mobile');
    const burgerIcon = burger.querySelector('.nav-hamburger-icon');

    function setMobile(open) {
        mobile.classList.toggle('is-open', open);
        burger.setAttribute('aria-expanded', open ? 'true' : 'false');
        burger.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
        burgerIcon.textContent = open ? 'close' : 'menu';
    }

    burger.addEventListener('click', () => setMobile(!mobile.classList.contains('is-open')));

    mobile.querySelectorAll('.nav-mobile-parent').forEach((parent) => {
        const sub = parent.nextElementSibling;
        parent.addEventListener('click', () => {
            const willOpen = !parent.classList.contains('is-open');
            mobile.querySelectorAll('.nav-mobile-parent').forEach((other) => {
                const otherSub = other.nextElementSibling;
                other.classList.remove('is-open');
                otherSub.style.maxHeight = '0px';
            });
            if (willOpen) {
                parent.classList.add('is-open');
                sub.style.maxHeight = sub.scrollHeight + 'px';
            }
        });
    });

    // Reposition / re-measure on resize, font load and page load
    function reMeasure() {
        measurePanels();
        if (activeId) {
            const item = document.getElementById('nav-top-' + activeId);
            if (item) positionWell(item);
        }
    }

    let resizeTick = false;
    window.addEventListener('resize', () => {
        if (!resizeTick) {
            resizeTick = true;
            requestAnimationFrame(() => {
                reMeasure();
                resizeTick = false;
            });
        }
    });
    if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(reMeasure);
        // Google Fonts loads unicode-range subsets lazily and can swap
        // glyphs AFTER fonts.ready resolves, so re-measure whenever any
        // font load finishes (width is width: max-content, but panel
        // height and the triangle position must stay in sync).
        document.fonts.addEventListener('loadingdone', reMeasure);
    }
    window.addEventListener('load', () => {
        reMeasure();
        // fonts.ready can resolve before webfonts are actually applied to
        // layout, so re-measure a couple of beats after load as well.
        setTimeout(reMeasure, 400);
        setTimeout(reMeasure, 1200);
    });

    measurePanels();
})();